import { useState, useMemo } from "react";
import { detectPostType } from "../utils/postTypeUtils";

/**
 * usePostTypeFilters - Hook for managing post type filter state and logic
 * Handles filtering posts by type (glow, pride, grateful)
 */
export const usePostTypeFilters = (posts = []) => {
	const [postTypeFilters, setPostTypeFilters] = useState({
		glow: false,
		pride: false,
		grateful: false,
	});

	// Check if any post type filter is active
	const hasActiveFilters = useMemo(
		() => Object.values(postTypeFilters).some((value) => value === true),
		[postTypeFilters]
	);

	// Toggle post type filter
	const togglePostTypeFilter = (type) => {
		setPostTypeFilters((prev) => ({
			...prev,
			[type]: !prev[type],
		}));
	};

	// Clear all post type filters
	const clearPostTypeFilters = () => {
		setPostTypeFilters({
			glow: false,
			pride: false,
			grateful: false,
		});
	};

	// Filter posts based on selected post types
	const filteredPosts = useMemo(() => {
		if (!hasActiveFilters) {
			// No filters active, show all posts
			return posts;
		}
		const filtered = posts.filter((post) => {
			const postType = detectPostType(post);
			const isFiltered = postTypeFilters[postType] === true;
			// Debug logging - log first few posts to see what's happening
			if (process.env.NODE_ENV === "development" && posts.indexOf(post) < 3) {
				console.log("🔍 Filter check:", {
					postType,
					filterState: postTypeFilters[postType],
					isFiltered,
					hasPrideID: !!post.PrideID,
					prideIDValue: post.PrideID,
					hasGratitudeID: !!post.GratitudeID,
					gratitudeIDValue: post.GratitudeID,
					hasGlowID: !!post.GlowID,
					glowIDValue: post.GlowID,
					hasType: !!post.Type,
					typeValue: post.Type,
					fullPost: post,
				});
			}
			return isFiltered;
		});
		console.log("Filtered posts:", {
			totalPosts: posts.length,
			filteredCount: filtered.length,
			activeFilters: postTypeFilters,
		});
		return filtered;
	}, [posts, postTypeFilters, hasActiveFilters]);

	return {
		postTypeFilters,
		hasActiveFilters,
		filteredPosts,
		togglePostTypeFilter,
		clearPostTypeFilters,
	};
};

export default usePostTypeFilters;
